import React, { useEffect, useRef } from "react";
import "./Slider.css";

const DRAG_MULTIPLIER = 1.25;
const FRICTION = 0.95;
const MIN_VELOCITY = 0.35;

const Slider = ({
    children,
    isText,
    itemWidth,
    gap,
    padding,
    ariaLabel,
}) => {
    const sliderRef = useRef(null);

    const dragRef = useRef({
        isDown: false,
        isDragging: false,
        startX: 0,
        startScrollLeft: 0,
        lastX: 0,
        lastTime: 0,
        velocity: 0,
        raf: null,
    });

    const childrenArray = React.Children.toArray(children);

    const isInteractiveElement = (target) => {
        return !!target.closest("button, a, input, textarea, select, audio, video");
    };

    const stopMomentum = () => {
        const state = dragRef.current;
        if (state.raf) {
            cancelAnimationFrame(state.raf);
            state.raf = null;
        }
    };

    const setDraggingClass = (value) => {
        const slider = sliderRef.current;
        if (!slider) return;
        slider.classList.toggle("dragging", value);
    };

    const startMomentum = () => {
        const slider = sliderRef.current;
        const state = dragRef.current;
        if (!slider) return;

        stopMomentum();

        let velocity = state.velocity;

        const step = () => {
            const currentSlider = sliderRef.current;
            if (!currentSlider) return;

            if (Math.abs(velocity) < MIN_VELOCITY) {
                state.raf = null;
                return;
            }

            const prev = currentSlider.scrollLeft;
            currentSlider.scrollLeft -= velocity;

            if (currentSlider.scrollLeft === prev) {
                state.raf = null;
                return;
            }

            velocity *= FRICTION;
            state.raf = requestAnimationFrame(step);
        };

        state.raf = requestAnimationFrame(step);
    };

    const beginDrag = (clientX) => {
        const slider = sliderRef.current;
        const state = dragRef.current;
        if (!slider) return;

        stopMomentum();

        state.isDown = true;
        state.isDragging = false;
        state.startX = clientX;
        state.startScrollLeft = slider.scrollLeft;
        state.lastX = clientX;
        state.lastTime = performance.now();
        state.velocity = 0;
    };

    const moveDrag = (clientX) => {
        const slider = sliderRef.current;
        const state = dragRef.current;
        if (!slider || !state.isDown) return;

        const dx = clientX - state.startX;

        if (Math.abs(dx) > 3) {
            state.isDragging = true;
            setDraggingClass(true);
        }

        slider.scrollLeft = state.startScrollLeft - dx * DRAG_MULTIPLIER;

        const now = performance.now();
        const dt = now - state.lastTime;

        if (dt > 0) {
            const deltaX = clientX - state.lastX;
            state.velocity = deltaX / dt * 16;
        }

        state.lastX = clientX;
        state.lastTime = now;
    };

    const endDrag = () => {
        const state = dragRef.current;
        if (!state.isDown) return;

        state.isDown = false;
        setDraggingClass(false);

        if (state.isDragging) {
            startMomentum();
        }

        requestAnimationFrame(() => {
            state.isDragging = false;
        });
    };

    const onMouseDown = (e) => {
        if (isInteractiveElement(e.target)) return;
        beginDrag(e.clientX);
    };

    const onMouseMove = (e) => {
        if (!dragRef.current.isDown) return;
        e.preventDefault();
        moveDrag(e.clientX);
    };

    const onTouchStart = (e) => {
        if (isInteractiveElement(e.target)) return;
        if (!e.touches[0]) return;
        beginDrag(e.touches[0].clientX);
    };

    const onTouchMove = (e) => {
        if (!dragRef.current.isDown) return;
        if (!e.touches[0]) return;
        moveDrag(e.touches[0].clientX);
    };

    const onClickCapture = (e) => {
        if (dragRef.current.isDragging) {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    useEffect(() => {
        const handleMouseUp = () => endDrag();

        window.addEventListener("mousemove", onMouseMove, { passive: false });
        window.addEventListener("mouseup", handleMouseUp);
        window.addEventListener("touchmove", onTouchMove, { passive: true });
        window.addEventListener("touchend", endDrag);
        window.addEventListener("touchcancel", endDrag);

        return () => {
            stopMomentum();
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
            window.removeEventListener("touchmove", onTouchMove);
            window.removeEventListener("touchend", endDrag);
            window.removeEventListener("touchcancel", endDrag);
        };
    }, []);

    return (
        <div
            className="reviews-wrapper"
            role={ariaLabel ? "region" : undefined}
            aria-roledescription={ariaLabel ? "carousel" : undefined}
            aria-label={ariaLabel}
        >
            <div
                ref={sliderRef}
                className="reviews-slider"
                style={{
                    "--slider-gap":
                        typeof gap === "number" ? `${gap}px` : gap,
                    "--slider-padding": padding,
                }}
                onMouseDown={onMouseDown}
                onTouchStart={onTouchStart}
                onClickCapture={onClickCapture}
                onDragStart={(event) => event.preventDefault()}
            >
                {childrenArray.map((child, index) => (
                    <div
                        className={`review-card ${isText ? "review__text" : ""}`}
                        key={index}
                        style={{
                            "--slider-item-width":
                                typeof itemWidth === "number"
                                    ? `${itemWidth}px`
                                    : itemWidth,
                        }}
                    >
                        {child}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Slider;
